import { onScopeDispose } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useModal } from '@/composition/use-modal';
import { i18n } from '@/plugins/i18n';
import { registerReusedPageLeaveGuard } from '@/utils/navigation.utils';

type TLeaveGuard = () => Promise<boolean>;

/** The guard of the mounted setup page (both setup routes are single-instance pages). */
let activeLeaveGuard: TLeaveGuard | null = null;

/**
 * For the routed page's `beforeRouteLeave`, declared in its second
 * `<script lang="ts">` block (vue-router 3 has no `onBeforeRouteLeave`
 * composable; the `RestaurantFloorPlanPage.vue` pattern): true when leaving
 * is allowed.
 */
export function confirmSetupLeave(): Promise<boolean> {
  return activeLeaveGuard ? activeLeaveGuard() : Promise.resolve(true);
}

export interface IUseSetupLeaveGuard {
  /** True at once when nothing is being saved; otherwise one «Estamos guardando» dialog at a time. */
  confirmLeave(): Promise<boolean>;
}

/**
 * Leave guard of the setup and import pages (plan §4.0): while something is
 * still being saved (`isBusy`), closing the tab prompts and leaving the route
 * asks «Estamos guardando»; otherwise leaving is silent (everything is saved).
 */
export function useSetupLeaveGuard(isBusy: () => boolean): IUseSetupLeaveGuard {
  const modal = useModal();
  let confirmation: Promise<boolean> | null = null;
  const t = (key: string) => String(i18n.t(key));

  function confirmLeave(): Promise<boolean> {
    if (!isBusy()) return Promise.resolve(true);
    if (!confirmation)
      confirmation = modal
        .confirm({
          title: t('storeSetup.leave.title'),
          message: t('storeSetup.leave.message'),
          acceptText: t('storeSetup.leave.accept'),
          cancelText: t('storeSetup.leave.cancel'),
          acceptColor: 'error',
        })
        .catch(() => false)
        .finally(() => {
          confirmation = null;
        });
    return confirmation;
  }

  useEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (!isBusy()) return;
    event.preventDefault();
    event.returnValue = '';
  });

  activeLeaveGuard = confirmLeave;
  const unregister = registerReusedPageLeaveGuard(confirmLeave);
  onScopeDispose(() => {
    unregister();
    if (activeLeaveGuard === confirmLeave) activeLeaveGuard = null;
  });

  return { confirmLeave };
}
